// react
import { ChangeEvent, Dispatch, MouseEvent, SetStateAction, useContext, useState } from 'react';

// types
import { AllocationPayload, ErrorCause, ExchangeRateByDate, Feedback } from '../../../@types';

// i18n
import { useTranslation } from 'react-i18next';

// context
import { ContextBanner } from '../../../App.context';
import { ContextProject } from '../../pages/Project/Project.context';

// hooks
import { useExchangeRates } from '../../../hooks/useExchangeRates';

// mui
import { Alert, AlertTitle, Grid } from '@mui/material';

// fields
import { Input } from '../../fields/Input/Input';
import { Allocations } from '../../fields/Allocations/Allocations';

// config
import { featureFlags } from '../../../config/featureFlags';

// utils
import { formatCurrency } from '../../../utils/currency';

// props
export interface FormAddCategoryValues {
	name: string;
	allocations: AllocationPayload[];
	exchangeRates: ExchangeRateByDate[];
}

export interface FormAddCategoryProps {
	onClose?: () => void;
	onSuccess?: () => void;
	setIsLoading?: Dispatch<SetStateAction<boolean>>;
}

export const FormAddCategory = ({ onClose, onSuccess, setIsLoading }: FormAddCategoryProps) => {
	// hooks
	const { t } = useTranslation();

	// context
	const { setBanner } = useContext(ContextBanner);
	const { project } = useContext(ContextProject);
	const { getCurrenciesForExchangeRates } = useExchangeRates();

	// session
	const accessToken = sessionStorage.getItem('accessToken');
	const idToken = sessionStorage.getItem('idToken');

	// state
	const [values, setValues] = useState<FormAddCategoryValues>(() => {
		const reportingCurrency =
			project?.currencies?.find((c) => c.type === 'REPORTING')?.currency || '';
		const currenciesForExchangeRates = getCurrenciesForExchangeRates({
			currency: reportingCurrency,
		});
		const exchangeRatesDefault = currenciesForExchangeRates
			.map((currency) => ({
				currency,
				dateExchangeRate: '',
				exchangeRate: 0,
			}))
			.sort((a, b) => a.currency.localeCompare(b.currency));

		return {
			name: '',
			allocations: [],
			exchangeRates: exchangeRatesDefault,
		};
	});
	const [feedback, setFeedback] = useState<Feedback | null>(null);

	// vars
	const idForm = 'form-add-category';

	const handleSubmit = async (e: MouseEvent<HTMLFormElement>) => {
		e.preventDefault();

		const form = document.querySelector(`#${idForm}`) as HTMLFormElement;
		const fd = new FormData(form);
		const entries = Object.fromEntries(fd.entries());

		try {
			// clear feedback
			setFeedback(null);

			// Validate: check for duplicate loans
			const loansWithValues = values.allocations.filter((a) => a.loan !== null).map((a) => a.loan);
			const duplicateLoans = loansWithValues.filter(
				(loan, index) => loansWithValues.indexOf(loan) !== index
			);

			if (duplicateLoans.length > 0) {
				setFeedback({
					alert: {
						children: t('error.duplicateLoanAllocations'),
					},
				});
				return;
			}

			// set loader
			if (setIsLoading) {
				setIsLoading(true);
			}

			const fetchAddCategory = await fetch(`${process.env.REACT_APP_API_URL}/categories`, {
				method: 'POST',
				headers: {
					Authorization: `Bearer ${accessToken}`,
					User: String(idToken),
				},
				body: JSON.stringify({
					name: values.name,
					project: project?.id,
					allocations: values.allocations
						.filter((a) => a.amount && a.loan !== 0)
						.map((allocation) => ({
							amount: Number(allocation.amount),
							conversions: allocation.conversions,
							currency: allocation.currency,
							loan: Number(allocation.loan),
						})),
				}),
			});
			const resAddCategory = await fetchAddCategory.json();

			if (resAddCategory.error) {
				throw new Error(resAddCategory.error.message, {
					cause: {
						details: resAddCategory.error.details,
						id: resAddCategory.error.id,
					},
				});
			}

			if (onSuccess) {
				onSuccess();
			}

			// set success banner
			setBanner({
				hasClose: true,
				message: t('alert.categoryCreated', { name: entries.name }),
				severity: 'success',
			});

			// close dialog
			if (onClose) {
				onClose();
			}
		} catch (error) {
			const err = error as Error;
			const cause = err.cause as ErrorCause;

			const feedbackNew = {
				alert: {
					children: t('error.default'),
				},
			};

			if (cause) {
				if (cause.id === 'CATEGORY_UNAUTHORIZED_POST') {
					feedbackNew.alert.children = t('error.unauthorized');
				} else if (cause.id === 'CATEGORY_ALLOCATION_INVALID_AMOUNT') {
					feedbackNew.alert.children = t('error.allocationInvalidAmount');
				} else if (cause.id === 'CATEGORY_ALLOCATION_CURRENCY_REQUIRED') {
					feedbackNew.alert.children = t('error.allocationCurrencyRequired');
				} else if (cause.id === 'CATEGORY_ALLOCATION_LOAN_NOT_FOUND') {
					feedbackNew.alert.children = t('error.allocationLoanNotFound');
				} else if (cause.id === 'CATEGORY_ALLOCATION_EXCEEDS_LOAN_AMOUNT') {
					feedbackNew.alert.children = t('error.allocationExceedsLoanAmount', {
						currency: cause.details.currency,
						loanTotal: formatCurrency({
							currency: cause.details.currency,
							value: cause.details.loan?.total || 0,
						}),
						total: formatCurrency({
							currency: cause.details.currency,
							value: cause.details.total || 0,
						}),
					});
				} else if (cause.id === 'PROJECT_REPORTING_CURRENCY_NOT_FOUND') {
					feedbackNew.alert.children = t('error.reportingCurrencyNotFound');
				}
			}

			// set feedback
			setFeedback(feedbackNew);
		} finally {
			// set loader
			if (setIsLoading) {
				setIsLoading(false);
			}
		}
	};

	const handleChangeValue = (e: ChangeEvent<HTMLInputElement>) => {
		const { name, value } = e.target as HTMLInputElement;
		setValues((prevValues) => ({
			...prevValues,
			[name]: value,
		}));
	};

	const handleChangeAllocations = (allocations: AllocationPayload[]) => {
		setValues((prevValues) => ({
			...prevValues,
			allocations,
		}));
	};

	const handleChangeExchangeRates = (exchangeRates: ExchangeRateByDate[]) => {
		setValues((prevValues) => {
			// Recalculate all allocation conversions with new exchange rates
			const updatedAllocations = prevValues.allocations.map((allocation) => ({
				...allocation,
				conversions: exchangeRates.map((rate) => ({
					amount:
						rate.exchangeRate > 0 ? Number((allocation.amount / rate.exchangeRate).toFixed(2)) : 0,
					currency: rate.currency,
					dateExchangeRate: rate.dateExchangeRate,
					exchangeRate: rate.exchangeRate,
					tax: allocation.conversions?.find((c) => c.currency === rate.currency)?.tax || 0,
				})),
			}));

			return {
				...prevValues,
				allocations: updatedAllocations,
				exchangeRates,
			};
		});
	};

	return (
		<form id={idForm} name="formAddCategory" onSubmit={handleSubmit}>
			<Grid container spacing={2}>
				<Grid item xs={12}>
					<Input
						id={`${idForm}-name`}
						label={t('name')}
						name="name"
						onChange={handleChangeValue}
						required={true}
						value={values.name}
					/>
				</Grid>
				{featureFlags['IFRS'] && (
					<Grid item xs={12}>
						<Allocations
							allocations={values.allocations}
							exchangeRates={values.exchangeRates}
							idForm={idForm}
							onChange={handleChangeAllocations}
							onChangeExchangeRates={handleChangeExchangeRates}
						/>
					</Grid>
				)}
				{feedback && (
					<Grid item xs={12}>
						<Alert severity="error" {...feedback.alert}>
							{feedback.title && <AlertTitle {...feedback.title} />}
							{feedback.alert.children}
						</Alert>
					</Grid>
				)}
			</Grid>
		</form>
	);
};
